/**
 * Firestore helpers for Blog Articles CMS.
 * Collections: artifacts/{appId}/blogArticles/{id}
 * Subcollections:
 *   artifacts/{appId}/blogArticles/{id}/likes/{userId}
 *   artifacts/{appId}/blogArticles/{id}/comments/{id}
 */

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  increment,
  limit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  startAfter,
  updateDoc,
  where,
  type DocumentData,
  type DocumentSnapshot,
  type QueryDocumentSnapshot,
  type Timestamp,
} from 'firebase/firestore';
import { db, appId } from './firebase';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type BlogArticleStatus = 'draft' | 'published' | 'unpublished';

export interface BlogArticle {
  id: string;
  title: string;
  slug: string;
  body: string;
  excerpt: string;
  featuredImage?: string | null;
  status: BlogArticleStatus;
  authorId: string;
  authorName: string;
  authorEmail?: string | null;
  publishedAt: Timestamp | Date | null;
  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;
  tags: string[];
  commentsEnabled: boolean;
  /** When true, the public-facing article page shows the view counter. */
  showViewCount: boolean;
  viewCount: number;
  likeCount: number;
  metaDescription?: string | null;
  /** When true, article appears in the featured articles list and embed. */
  featured?: boolean;
}

export interface BlogArticlePayload {
  title: string;
  slug: string;
  body: string;
  excerpt: string;
  featuredImage?: string | null;
  status: BlogArticleStatus;
  authorId: string;
  authorName: string;
  authorEmail?: string | null;
  publishedAt?: Timestamp | Date | null;
  tags: string[];
  commentsEnabled: boolean;
  showViewCount: boolean;
  metaDescription?: string | null;
  featured?: boolean;
}

export interface BlogComment {
  id: string;
  articleId: string;
  authorName: string;
  authorEmail?: string | null;
  body: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: Timestamp | Date;
}

export interface BlogLike {
  userId: string;
  createdAt: Timestamp | Date;
}

// ---------------------------------------------------------------------------
// Embed code constant
// ---------------------------------------------------------------------------

/** Paste this into any CMS page body to embed the blog articles listing. */
export const BLOG_ARTICLES_EMBED_CODE = '<div data-smbprime-embed="blog-articles"></div>';

/** Paste this into any CMS page body to embed only featured (published) articles. */
export const BLOG_FEATURED_ARTICLES_EMBED_CODE = '<div data-smbprime-embed="blog-articles-featured"></div>';

// ---------------------------------------------------------------------------
// Collection refs
// ---------------------------------------------------------------------------

function articlesRef() {
  return collection(db, 'artifacts', appId, 'blogArticles');
}

function articleRef(id: string) {
  return doc(db, 'artifacts', appId, 'blogArticles', id);
}

function likesRef(articleId: string) {
  return collection(db, 'artifacts', appId, 'blogArticles', articleId, 'likes');
}

function likeRef(articleId: string, userId: string) {
  return doc(db, 'artifacts', appId, 'blogArticles', articleId, 'likes', userId);
}

function commentsRef(articleId: string) {
  return collection(db, 'artifacts', appId, 'blogArticles', articleId, 'comments');
}

function commentRef(articleId: string, commentId: string) {
  return doc(db, 'artifacts', appId, 'blogArticles', articleId, 'comments', commentId);
}

// ---------------------------------------------------------------------------
// Mappers
// ---------------------------------------------------------------------------

function mapArticle(snap: DocumentSnapshot<DocumentData> | QueryDocumentSnapshot<DocumentData>): BlogArticle {
  const data = snap.data() as Omit<BlogArticle, 'id'>;
  return { id: snap.id, ...data };
}

function mapComment(snap: DocumentSnapshot<DocumentData> | QueryDocumentSnapshot<DocumentData>, articleId: string): BlogComment {
  const data = snap.data() as Omit<BlogComment, 'id' | 'articleId'>;
  return { id: snap.id, articleId, ...data };
}

// ---------------------------------------------------------------------------
// Article CRUD
// ---------------------------------------------------------------------------

/** Fetch all articles (admin list, ordered by createdAt desc). */
export async function getAllBlogArticles(): Promise<BlogArticle[]> {
  const q = query(articlesRef(), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(mapArticle);
}

/**
 * Fetch published articles with cursor-based pagination.
 * Requires a composite index on (status ASC, publishedAt DESC).
 */
export async function getPublishedArticles(
  pageLimit = 9,
  cursor?: QueryDocumentSnapshot<DocumentData> | null
): Promise<{ articles: BlogArticle[]; lastDoc: QueryDocumentSnapshot<DocumentData> | null }> {
  let q = query(
    articlesRef(),
    where('status', '==', 'published'),
    orderBy('publishedAt', 'desc'),
    limit(pageLimit)
  );
  if (cursor) {
    q = query(
      articlesRef(),
      where('status', '==', 'published'),
      orderBy('publishedAt', 'desc'),
      startAfter(cursor),
      limit(pageLimit)
    );
  }
  const snap = await getDocs(q);
  const lastDoc = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null;
  return { articles: snap.docs.map(mapArticle), lastDoc };
}

/**
 * Fetch published articles that are marked featured, with cursor-based pagination.
 * Requires a composite index on (status ASC, featured ASC, publishedAt DESC).
 */
export async function getFeaturedPublishedArticles(
  pageLimit = 9,
  cursor?: QueryDocumentSnapshot<DocumentData> | null
): Promise<{ articles: BlogArticle[]; lastDoc: QueryDocumentSnapshot<DocumentData> | null }> {
  let q = query(
    articlesRef(),
    where('status', '==', 'published'),
    where('featured', '==', true),
    orderBy('publishedAt', 'desc'),
    limit(pageLimit)
  );
  if (cursor) {
    q = query(
      articlesRef(),
      where('status', '==', 'published'),
      where('featured', '==', true),
      orderBy('publishedAt', 'desc'),
      startAfter(cursor),
      limit(pageLimit)
    );
  }
  const snap = await getDocs(q);
  const lastDoc = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null;
  return { articles: snap.docs.map(mapArticle), lastDoc };
}

/** Fetch a single published article by slug (public facing). */
export async function getPublishedArticleBySlug(slug: string): Promise<BlogArticle | null> {
  const q = query(articlesRef(), where('slug', '==', slug), where('status', '==', 'published'));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return mapArticle(snap.docs[0]);
}

/** Fetch any article by slug (admin facing — ignores status). */
export async function getArticleBySlugForAdmin(slug: string): Promise<BlogArticle | null> {
  const q = query(articlesRef(), where('slug', '==', slug));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return mapArticle(snap.docs[0]);
}

/** Fetch any article by id. */
export async function getArticleById(id: string): Promise<BlogArticle | null> {
  const snap = await getDoc(articleRef(id));
  if (!snap.exists()) return null;
  return mapArticle(snap);
}

/** Check if a slug is already taken (used for unique slug validation). */
export async function isSlugTaken(slug: string, excludeId?: string): Promise<boolean> {
  const q = query(articlesRef(), where('slug', '==', slug));
  const snap = await getDocs(q);
  if (snap.empty) return false;
  if (excludeId && snap.docs.length === 1 && snap.docs[0].id === excludeId) return false;
  return true;
}

/** Create a new blog article. Returns the new document id. */
export async function createBlogArticle(payload: BlogArticlePayload): Promise<string> {
  const docRef = await addDoc(articlesRef(), {
    ...payload,
    featured: payload.featured ?? false,
    viewCount: 0,
    likeCount: 0,
    publishedAt: payload.status === 'published' ? (payload.publishedAt ?? serverTimestamp()) : null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return docRef.id;
}

/** Update an existing blog article. */
export async function updateBlogArticle(id: string, payload: Partial<BlogArticlePayload>): Promise<void> {
  const updates: Record<string, unknown> = {
    ...payload,
    updatedAt: serverTimestamp(),
  };
  // Auto-set publishedAt when status transitions to published
  if (payload.status === 'published' && !payload.publishedAt) {
    const current = await getArticleById(id);
    if (!current?.publishedAt) {
      updates.publishedAt = serverTimestamp();
    }
  }
  await updateDoc(articleRef(id), updates);
}

/** Delete a blog article and its subcollections (likes + comments). */
export async function deleteBlogArticle(id: string): Promise<void> {
  // Delete comments
  const commentsSnap = await getDocs(commentsRef(id));
  for (const d of commentsSnap.docs) {
    await deleteDoc(d.ref);
  }
  // Delete likes
  const likesSnap = await getDocs(likesRef(id));
  for (const d of likesSnap.docs) {
    await deleteDoc(d.ref);
  }
  await deleteDoc(articleRef(id));
}

// ---------------------------------------------------------------------------
// View tracking
// ---------------------------------------------------------------------------

/**
 * Increment the view count for an article.
 * Call this once per article detail page mount, gated by sessionStorage to avoid
 * counting refreshes as new views.
 */
export async function incrementViewCount(articleId: string): Promise<void> {
  const key = `viewed_${articleId}`;
  if (sessionStorage.getItem(key)) return;
  sessionStorage.setItem(key, '1');
  await updateDoc(articleRef(articleId), { viewCount: increment(1) });
}

// ---------------------------------------------------------------------------
// Likes
// ---------------------------------------------------------------------------

/** Check if a user (identified by userId or anonymous key) has liked an article. */
export async function hasUserLiked(articleId: string, userId: string): Promise<boolean> {
  const snap = await getDoc(likeRef(articleId, userId));
  return snap.exists();
}

/**
 * Toggle like for a user.
 * Uses a transaction to atomically update the likes subcollection and likeCount.
 * Returns true if the article is now liked, false if unliked.
 */
export async function toggleLike(articleId: string, userId: string): Promise<boolean> {
  const likeDocRef = likeRef(articleId, userId);
  const articleDocRef = articleRef(articleId);

  let nowLiked = false;
  await runTransaction(db, async (tx) => {
    const likeSnap = await tx.get(likeDocRef);
    if (likeSnap.exists()) {
      tx.delete(likeDocRef);
      tx.update(articleDocRef, { likeCount: increment(-1) });
      nowLiked = false;
    } else {
      tx.set(likeDocRef, { userId, createdAt: serverTimestamp() });
      tx.update(articleDocRef, { likeCount: increment(1) });
      nowLiked = true;
    }
  });
  return nowLiked;
}

// ---------------------------------------------------------------------------
// Comments
// ---------------------------------------------------------------------------

/** Fetch approved comments for an article, ordered by createdAt asc. */
export async function getApprovedComments(articleId: string): Promise<BlogComment[]> {
  const q = query(
    commentsRef(articleId),
    where('status', '==', 'approved'),
    orderBy('createdAt', 'asc')
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => mapComment(d, articleId));
}

/** Fetch all comments for an article (admin facing). */
export async function getAllComments(articleId: string): Promise<BlogComment[]> {
  const q = query(commentsRef(articleId), orderBy('createdAt', 'asc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => mapComment(d, articleId));
}

/**
 * Add a comment to an article.
 * Comments are stored with status 'pending' and must be approved by an admin.
 */
export async function addComment(
  articleId: string,
  data: { authorName: string; authorEmail?: string | null; body: string }
): Promise<string> {
  const docRef = await addDoc(commentsRef(articleId), {
    ...data,
    status: 'pending',
    createdAt: serverTimestamp(),
  });
  return docRef.id;
}

/** Update a comment's status (admin action). */
export async function updateCommentStatus(
  articleId: string,
  commentId: string,
  status: 'pending' | 'approved' | 'rejected'
): Promise<void> {
  await updateDoc(commentRef(articleId, commentId), { status });
}

/** Delete a comment. */
export async function deleteComment(articleId: string, commentId: string): Promise<void> {
  await deleteDoc(commentRef(articleId, commentId));
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Generate a URL-safe slug from a title string. */
export function slugify(title: string): string {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Estimate read time in minutes from HTML body string. */
export function estimateReadTime(html: string): number {
  const text = html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  const words = text.split(' ').filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 200));
}

/** Format a Firestore Timestamp or Date to a human-readable string. */
export function formatDate(ts: Timestamp | Date | null | undefined): string {
  if (!ts) return '';
  const date = ts instanceof Date ? ts : ts.toDate?.() ?? new Date();
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

/** Anonymous-user identifier stored in localStorage (for anonymous likes). */
export function getAnonymousUserId(): string {
  const key = 'smbprime_anon_uid';
  let id = localStorage.getItem(key);
  if (!id) {
    id = `anon_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(key, id);
  }
  return id;
}

/** Return the doc ref snapshot type (re-exported for convenience). */
export type { QueryDocumentSnapshot };
